/**
 * Gestor de estado global centralizado
 * 
 * Estructura:
 * {
 *   ingredientes: { byId: {}, allIds: [] },
 *   recetas: { byId: {}, allIds: [] },
 *   ventas: { byId: {}, allIds: [] },
 *   ui: { currentModule: null, isLoading: false },
 *   sync: { pendingOps: [], lastSyncTime: null, isSyncing: false }
 * }
 * 
 * Principios:
 * - Estado es inmutable (crear nuevos objetos)
 * - Solo cambios vía setState()
 * - Cambios emiten eventos
 * - Persistencia automática en localStorage
 */

import eventBus, { EVENTS } from './eventBus.js';

class StateManager {
  constructor() {
    // Estado privado
    this.#state = this.#initializeState();
    
    // Subscribers a cambios de estado
    this.#subscribers = new Map();

    // Cargar estado persistido desde localStorage
    this.#loadFromStorage();
  }

  #state;
  #subscribers;

  /**
   * Inicializar estado con estructura vacía
   */
  #initializeState() {
    return {
      ingredientes: {
        byId: {},
        allIds: [],
        lastUpdated: null
      },
      recetas: {
        byId: {},
        allIds: [],
        lastUpdated: null
      },
      ventas: {
        byId: {},
        allIds: [],
        lastUpdated: null
      },
      products: {
        byId: {},
        allIds: [],
        lastUpdated: null
      },
      movements: {
        byId: {},
        allIds: [],
        lastUpdated: null
      },
      banners: [],
      ui: {
        currentModule: 'ingredientes',
        isLoading: false,
        error: null
      },
      sync: {
        pendingOps: [],
        lastSyncTime: null,
        isSyncing: false
      }
    };
  }

  /**
   * Cargar estado desde localStorage
   */
  #loadFromStorage() {
    try {
      const stored = localStorage.getItem('faki_state');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge con estado inicial (en caso de nuevas propiedades)
        this.#state = { ...this.#state, ...parsed };
        console.log('✓ Estado cargado desde localStorage');
      }
    } catch (error) {
      console.error('Error cargando estado:', error);
    }
  }

  /**
   * Guardar estado en localStorage
   */
  #saveToStorage() {
    try {
      localStorage.setItem('faki_state', JSON.stringify(this.#state));
    } catch (error) {
      console.error('Error guardando estado:', error);
      if (error.name === 'QuotaExceededError') {
        eventBus.emit(EVENTS['app:error'], {
          message: 'Cuota de almacenamiento excedida',
          type: 'storage'
        });
      }
    }
  }

  /**
   * Obtener estado completo (copia superficial)
   */
  getState() {
    return { ...this.#state };
  }

  /**
   * Obtener parte específica del estado
   * @param {string} path - Ruta del estado (ej: "ingredientes.byId")
   */
  getStateAt(path) {
    const parts = path.split('.');
    let value = this.#state;

    for (const part of parts) {
      if (value && typeof value === 'object' && part in value) {
        value = value[part];
      } else {
        return undefined;
      }
    }

    return value;
  }

  /**
   * Actualizar estado en una ruta específica
   * @param {string} path - Ruta del estado (ej: "ingredientes.byId")
   * @param {*} value - Nuevo valor
   */
  setState(path, value) {
    const parts = path.split('.');
    const lastPart = parts.pop();

    // Navegar hasta el padre
    let current = this.#state;
    for (const part of parts) {
      if (!(part in current)) {
        current[part] = {};
      }
      current = current[part];
    }

    // Actualizar valor (comprobar si cambió)
    if (current[lastPart] !== value) {
      current[lastPart] = value;

      // Guardar en storage
      this.#saveToStorage();

      // Notificar subscribers
      this.#notifySubscribers(path, value);

      // Emitir evento genérico
      eventBus.emit('state:changed', {
        path,
        value,
        timestamp: new Date()
      });
    }
  }

  /**
   * Actualizar estado completo de una entidad
   * @param {string} entity - Tipo de entidad (ingredientes, recetas, etc)
   * @param {string} id - ID de la entidad
   * @param {Object} data - Datos de la entidad
   * @param {string} operationType - 'create', 'update', 'delete'
   */
  setEntity(entity, id, data, operationType = 'update') {
    const entityState = this.#state[entity];
    if (!entityState) return;

    if (operationType === 'delete') {
      delete entityState.byId[id];
      entityState.allIds = entityState.allIds.filter(aid => aid !== id);
    } else {
      // Crear o actualizar
      if (!entityState.allIds.includes(id)) {
        entityState.allIds.push(id);
      }
      entityState.byId[id] = {
        ...entityState.byId[id],
        ...data,
        id,
        updatedAt: new Date().toISOString()
      };
    }

    entityState.lastUpdated = new Date().toISOString();

    // Guardar en storage
    this.#saveToStorage();

    // Notificar
    this.#notifySubscribers(`${entity}.byId.${id}`, entityState.byId[id]);
    this.#notifySubscribers(entity, entityState);
  }

  /**
   * Obtener todas las entidades de un tipo
   * @param {string} entity - Tipo de entidad
   * @returns {Object[]} Array de entidades
   */
  getEntities(entity) {
    const entityState = this.#state[entity];
    if (!entityState) return [];

    return entityState.allIds.map(id => entityState.byId[id]);
  }

  /**
   * Obtener una entidad por ID
   * @param {string} entity - Tipo de entidad
   * @param {string} id - ID de la entidad
   * @returns {Object|null} La entidad o null
   */
  getEntity(entity, id) {
    const entityState = this.#state[entity];
    if (!entityState) return null;
    return entityState.byId[id] || null;
  }

  /**
   * Suscribirse a cambios en una ruta del estado
   * @param {string} path - Ruta a observar
   * @param {Function} callback - Ejecutar cuando cambie
   * @returns {Function} Función para desuscribirse
   */
  subscribe(path, callback) {
    if (!this.#subscribers.has(path)) {
      this.#subscribers.set(path, new Set());
    }

    const subscribers = this.#subscribers.get(path);
    subscribers.add(callback);

    // Retornar desuscriptor
    return () => {
      subscribers.delete(callback);
      if (subscribers.size === 0) {
        this.#subscribers.delete(path);
      }
    };
  }

  /**
   * Notificar a suscriptores de cambios
   */
  #notifySubscribers(path, value) {
    if (this.#subscribers.has(path)) {
      const subscribers = this.#subscribers.get(path);
      subscribers.forEach(callback => {
        try {
          callback(value);
        } catch (error) {
          console.error(`Error en subscriber de ${path}:`, error);
        }
      });
    }
  }

  /**
   * Limpiar estado (para testing)
   */
  clear() {
    this.#state = this.#initializeState();
    localStorage.removeItem('faki_state');
  }

  /**
   * Agregar operación pendiente a la cola de sincronización
   */
  addPendingOp(operation) {
    const ops = this.#state.sync.pendingOps;
    ops.push({
      ...operation,
      addedAt: new Date().toISOString()
    });
    this.#saveToStorage();
  }

  /**
   * Obtener operaciones pendientes
   */
  getPendingOps() {
    return [...this.#state.sync.pendingOps];
  }

  /**
   * Remover operación pendiente
   */
  removePendingOp(operationId) {
    this.#state.sync.pendingOps = this.#state.sync.pendingOps.filter(
      op => op.id !== operationId
    );
    this.#saveToStorage();
  }

  /**
   * Limpiar todas las operaciones pendientes
   */
  clearPendingOps() {
    this.#state.sync.pendingOps = [];
    this.#saveToStorage();
  }

  // Getters para acceso directo
  get banners() { return this.#state.banners; }
  set banners(value) { this.#state.banners = value; }
}

// Singleton global
const stateManager = new StateManager();

export default stateManager;
